// Cadência de follow-up (PLANO 4.3): 5min → 1h → 24h → 3d → 7d, configurável
// por clínica. Puro de propósito — o cálculo de "quando é o próximo contato"
// é regra de negócio e precisa ser testável sem Firestore.

export const CADENCIA_PADRAO = ['5m', '1h', '24h', '3d', '7d'];

const UNIDADES = { m: 60_000, h: 3_600_000, d: 86_400_000 };

/** Converte '24h' em milissegundos. Devolve null se o formato não for válido. */
export function paraMilissegundos(intervalo) {
  const casou = /^(\d+)([mhd])$/.exec(String(intervalo ?? '').trim());
  if (!casou) return null;
  return Number(casou[1]) * UNIDADES[casou[2]];
}

const ROTULOS = { m: 'minutos', h: 'horas', d: 'dias' };

/** Descreve '3d' como "3 dias" para a interface. */
export function descrever(intervalo) {
  const casou = /^(\d+)([mhd])$/.exec(String(intervalo ?? '').trim());
  if (!casou) return String(intervalo ?? '');
  const [, quantidade, unidade] = casou;
  const rotulo = ROTULOS[unidade];
  return `${quantidade} ${Number(quantidade) === 1 ? rotulo.replace(/s$/, '') : rotulo}`;
}

/**
 * Próximo passo da cadência para um lead com N tentativas já feitas.
 * Depois do último degrau a cadência se esgota — o lead deve ser trabalhado
 * manualmente ou marcado como perdido, não reagendado para sempre.
 */
export function proximoPasso(tentativas, cadencia = CADENCIA_PADRAO) {
  const passos = cadencia.filter((i) => paraMilissegundos(i) !== null);
  const indice = Math.max(0, Number(tentativas) || 0);
  if (indice >= passos.length) return null;
  return passos[indice];
}

/** Data/hora do próximo follow-up, ou null se a cadência acabou. */
export function proximaData(tentativas, cadencia = CADENCIA_PADRAO, agora = new Date()) {
  const passo = proximoPasso(tentativas, cadencia);
  if (!passo) return null;
  return new Date(agora.getTime() + paraMilissegundos(passo));
}

// ------------------------------------------------- pós-comparecimento (V4)

/**
 * Cadência de RETORNO depois que a pessoa compareceu à avaliação e ainda não
 * fechou: 1d → 3d → 7d → 15d, configurável por clínica
 * (`config.cadenciaPosComparecimento`). É mais lenta que a de primeiro contato
 * porque o assunto agora é decisão de compra, não "atende o telefone".
 */
export const CADENCIA_POS_COMPARECIMENTO_PADRAO = ['1d', '3d', '7d', '15d'];

/**
 * Etapas em que o lead está sendo trabalhado pelo negociador.
 *
 * ★ VG: `em_negociacao` saiu; **"Não fechou" é a etapa pós-comparecimento
 * VIVA** — é onde o negociador faz follow-up e de onde o lead pode ir para
 * Fechou. A cadência de retorno, a fila "negociação" e o tipo da tarefa seguem
 * daqui.
 */
export const ETAPAS_POS_COMPARECIMENTO = ['compareceu', 'nao_fechou'];

export const ehPosComparecimento = (etapa) => ETAPAS_POS_COMPARECIMENTO.includes(etapa);

/**
 * Qual cadência vale para este lead: pós-comparecimento nas etapas de
 * negociação; follow-up de primeiro contato nas demais. A config da clínica
 * vence o padrão nos dois casos.
 */
export function cadenciaParaLead(lead, config = null) {
  if (ehPosComparecimento(lead?.etapa)) {
    return config?.cadenciaPosComparecimento?.length
      ? config.cadenciaPosComparecimento
      : CADENCIA_POS_COMPARECIMENTO_PADRAO;
  }
  return config?.cadenciaFollowUp?.length ? config.cadenciaFollowUp : CADENCIA_PADRAO;
}

/**
 * Em que degrau da cadência o lead está.
 *
 * Primeiro contato: `tentativasContato` (cada ligação registrada avança). Pós-
 * comparecimento: RETORNOS CONCLUÍDOS — tarefas `tipo: 'retorno'` já concluídas
 * — porque ali o que avança a régua é ter feito o retorno, não ter discado.
 * Uma ligação avulsa registrada nessa fase não pula degrau.
 */
export function indiceDoPasso(lead, tarefas = []) {
  if (ehPosComparecimento(lead?.etapa)) {
    // ★ PC-2: retorno encerrado PELO SISTEMA (o gesto o tornou velho) não é retorno FEITO —
    // não avança a régua. Só a conclusão humana conta.
    return tarefas.filter(
      (t) => t?.tipo === 'retorno' && t?.concluida === true && t?.concluidaPorId !== 'sistema',
    ).length;
  }
  return Math.max(0, Number(lead?.tentativasContato) || 0);
}

/** Classifica uma tarefa pela data, para agrupar a fila do dia. */
export function classificarPrazo(dataHora, agora = new Date()) {
  if (!dataHora) return 'sem_data';
  const data = typeof dataHora.toDate === 'function' ? dataHora.toDate() : new Date(dataHora);
  if (Number.isNaN(data.getTime())) return 'sem_data';

  const fimDeHoje = new Date(agora);
  fimDeHoje.setHours(23, 59, 59, 999);
  const fimDeAmanha = new Date(fimDeHoje.getTime() + 86_400_000);

  if (data < agora) return 'atrasada';
  if (data <= fimDeHoje) return 'hoje';
  if (data <= fimDeAmanha) return 'amanha';
  return 'depois';
}

export const GRUPOS_PRAZO = [
  { id: 'atrasada', label: 'Atrasadas' },
  { id: 'hoje', label: 'Hoje' },
  { id: 'amanha', label: 'Amanhã' },
  { id: 'depois', label: 'Próximos dias' },
  { id: 'sem_data', label: 'Sem data' },
];
